<script lang="ts">
	//Services
	import { format } from '../../../../services/i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Helpers
	import { formatSnakeCaseToTitle, capFirstChar } from '../../../../helpers/utilities';
	import {
		get_gen_2_component_from_category,
		real_id,
		get_device_component_index,
		get_gen_2_component_channel_structure,
	} from '../../../../helpers/interface_helpers';
	import {
		convertSeconds,
		convertToSeconds,
		formatTwoDigits,
		formatTimeLabel,
		getTimeWheelTexts,
		getTimeWheelMsStep,
		getTimeWheelToggleText,
	} from '../../../../helpers/timer_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	//Stores
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	import type { Device } from 'src/types/Device';
	import type { SlotName } from 'src/types/Cury';
	//Design system components
	import { Button, Toggle, Text, TimeWheel } from '@Shelly/shelly-design-system';
	import { onMount } from 'svelte';
	//Components
	import AwayModeActiveAlert from 'src/components/Alerts/AwayModeActiveAlert.svelte';

	export let device: Device;

	const id = real_id(device.id);
	const component = get_gen_2_component_channel_structure(device);
	const slots: SlotName[] = ['left', 'right'];
	const subsettings: Array<'auto_on' | 'auto_off'> = ['auto_on', 'auto_off'];

	$: statusStore = getDeviceStatus(id);
	$: status = $statusStore;
	$: awayModeActive = status?.[component]?.away_mode || false;

	$: settingsStore = getDeviceSettings(id);
	$: timerSettings = $settingsStore?.[component]?.timer;

	let loading = {
		left: { auto_on: false, auto_off: false },
		right: { auto_on: false, auto_off: false },
	};

	let min = 0,
		max = 2147483;

	$: timersValues = {
		left: {
			auto_on: timerSettings?.left?.auto_on ?? false,
			auto_on_delay: timerSettings?.left?.auto_on_delay ?? 60,
			auto_off: timerSettings?.left?.auto_off ?? false,
			auto_off_delay: timerSettings?.left?.auto_off_delay ?? 60,
		},
		right: {
			auto_on: timerSettings?.right?.auto_on ?? false,
			auto_on_delay: timerSettings?.right?.auto_on_delay ?? 60,
			auto_off: timerSettings?.right?.auto_off ?? false,
			auto_off_delay: timerSettings?.right?.auto_off_delay ?? 60,
		},
	};

	let selectedHour = {
		left: { auto_on_delay: '00', auto_off_delay: '00' },
		right: { auto_on_delay: '00', auto_off_delay: '00' },
	};
	let selectedMinute = {
		left: { auto_on_delay: '00', auto_off_delay: '00' },
		right: { auto_on_delay: '00', auto_off_delay: '00' },
	};
	let selectedSecond = {
		left: { auto_on_delay: '00', auto_off_delay: '00' },
		right: { auto_on_delay: '00', auto_off_delay: '00' },
	};
	let selectedMs = {
		left: { auto_on_delay: undefined, auto_off_delay: undefined },
		right: { auto_on_delay: undefined, auto_off_delay: undefined },
	};

	let maxTimes = convertSeconds(max);

	const method = `${get_gen_2_component_from_category(device)}.SetConfig`;

	$: infoText = `${$format('timer_info_description', {
		values: {
			min: min,
			max: formatTimeLabel(
				maxTimes.hours,
				maxTimes.minutes,
				maxTimes.seconds,
				maxTimes.milliseconds,
			),
		},
	})}`;

	const getFormattedTime = (slot: SlotName, setting: string) => {
		const delayKey = `${setting}_delay` as 'auto_on_delay' | 'auto_off_delay';
		return formatTimeLabel(
			selectedHour[slot][delayKey],
			selectedMinute[slot][delayKey],
			selectedSecond[slot][delayKey],
			selectedMs[slot][delayKey],
		);
	};

	const getError = (slot: SlotName, setting: string) => {
		const delayKey = `${setting}_delay` as 'auto_on_delay' | 'auto_off_delay';
		return convertToSeconds(
			selectedHour[slot][delayKey],
			selectedMinute[slot][delayKey],
			selectedSecond[slot][delayKey],
			selectedMs[slot][delayKey],
		) > max
			? infoText
			: '';
	};

	onMount(() => {
		slots.forEach((slot) => {
			updateTimeValues(slot, 'auto_on_delay');
			updateTimeValues(slot, 'auto_off_delay');
		});
	});

	const getTimeInSeconds = (slot: SlotName, type: 'auto_on_delay' | 'auto_off_delay') => {
		return convertToSeconds(
			selectedHour[slot][type],
			selectedMinute[slot][type],
			selectedSecond[slot][type],
		);
	};

	const updateTimeValues = (slot: SlotName, type: 'auto_on_delay' | 'auto_off_delay') => {
		const { hours, minutes, seconds, milliseconds } = convertSeconds(timersValues[slot][type] || 0);
		selectedHour[slot][type] = formatTwoDigits(hours);
		selectedMinute[slot][type] = formatTwoDigits(minutes);
		selectedSecond[slot][type] = formatTwoDigits(seconds);
		selectedMs[slot][type] = milliseconds ? formatTwoDigits(milliseconds) : undefined;
	};

	const set_auto = async (slot: SlotName, setting: 'auto_on' | 'auto_off') => {
		loading[slot][setting] = true;
		try {
			const seconds = getTimeInSeconds(slot, `${setting}_delay` as any);
			const params = {
				id: get_device_component_index(device),
				config: {
					timer: {
						[slot]: {
							[setting]: timersValues[slot][setting],
							[`${setting}_delay`]: seconds,
						},
					},
				},
			};

			const res: SettingRequest = await gen2Request(device, method, params);
			const settingLabel = $format(setting);
			const successTitle = !timersValues[slot][setting]
				? $format('auto_turn_successfully_disable', {
						values: { auto_turn_state: settingLabel },
					})
				: `${$format(`${setting}_set_toast`)} ${formatTimeLabel(
						selectedHour[slot][`${setting}_delay`],
						selectedMinute[slot][`${setting}_delay`],
						selectedSecond[slot][`${setting}_delay`],
						selectedMs[slot][`${setting}_delay`],
					)}.`;
			const errorTitle = res?.errors?.message ? res?.errors?.message : $format('timer_err_toast');
			const result: any = await onSettingsResponse(res, device, {
				success: successTitle,
				danger: errorTitle,
			});
			if (result && !result.isok) {
				throw new Error(result.errors?.message || 'Timer settings failed.');
			}

			loading[slot][setting] = false;
		} catch (error) {
			console.error(error);
			loading[slot][setting] = false;
		}
	};

	const handle_toggler = async (slot: SlotName, setting: 'auto_on' | 'auto_off') => {
		if (loading[slot][setting]) return;
		timersValues[slot][setting] = !timersValues[slot][setting];
		await set_auto(slot, setting);
	};
</script>

{#if awayModeActive}
	<AwayModeActiveAlert {device} />
{:else}
	{#each slots as slot}
		<Text styleType="h2" class="mb-s text-bold">
			{capFirstChar($format(`label_${slot}_vial`, { default: `${slot} Vial` }))}
		</Text>
		{#each subsettings as setting, index}
			{#if index > 0}
				<hr class="mt-l mb-l" />
			{/if}

			<Toggle
				size="m"
				checked={timersValues[slot][setting]}
				disabled={loading[slot][setting]}
				on:click={() => handle_toggler(slot, setting)}
				id="toggle-timer-diff-{slot}-{setting}"
				aria-label={formatSnakeCaseToTitle($format(setting))}
			>
				<Text element="span" styleType="controls-small" class="text-white">
					{formatSnakeCaseToTitle($format(setting))}
				</Text>
			</Toggle>

			<Text styleType="tiny" class="text-white" style="margin-top: 2px">
				{$format(`${setting}_toggle_info`, {
					default:
						setting === 'auto_on'
							? 'Turns the device on automatically after the selected time'
							: 'Turns the device off automatically after the selected time',
				})}
			</Text>

			{#if timersValues[slot][setting]}
				<Text styleType="controls-small" class="mt-s-plus mb-s">
					{$format(`${setting}_time_label`, {
						default:
							setting === 'auto_on'
								? 'Turn the device on after'
								: 'Turn the device off after',
					})}
					<Text element="span" styleType="body-small" class="text-primary text-bold">
						{getFormattedTime(slot, setting)}
					</Text>
				</Text>

				<TimeWheel
					id="timewheel-diff-{slot}-{setting}"
					a11yProps={{
						inputAriaLabel: $format('label_time_wheel_input', {
							default: 'Time wheel input',
						}),
						buttonAriaLabel: $format('label_time_wheel_button', {
							default: 'Time wheel button',
						}),
						secondsColumnAriaLabel: $format('label_time_wheel_seconds_column', {
							default: 'Time wheel seconds column',
						}),
						secondPrefixAriaLabel: $format('label_time_wheel_second_prefix', {
							default: 'Time wheel second prefix',
						}),
						msColumnAriaLabel: $format('label_time_wheel_ms_column', {
							default: 'Time wheel ms column',
						}),
						msPrefixAriaLabel: $format('label_time_wheel_ms_prefix', {
							default: 'Time wheel ms prefix',
						}),
						hoursColumnAriaLabel: $format('label_time_wheel_hours_column', {
							default: 'Time wheel hours column',
						}),
						hourPrefixAriaLabel: $format('label_time_wheel_hour_prefix', {
							default: 'Time wheel hour prefix',
						}),
						minutesColumnAriaLabel: $format('label_time_wheel_minutes_column', {
							default: 'Time wheel minutes column',
						}),
						minutePrefixAriaLabel: $format('label_time_wheel_minute_prefix', {
							default: 'Time wheel minute prefix',
						}),
					}}
					class="mb-s"
					bind:selectedHour={selectedHour[slot][`${setting}_delay`]}
					bind:selectedMinute={selectedMinute[slot][`${setting}_delay`]}
					bind:selectedSecond={selectedSecond[slot][`${setting}_delay`]}
					bind:selectedMs={selectedMs[slot][`${setting}_delay`]}
					maxHours={maxTimes.hours}
					msToggleText={getTimeWheelToggleText()}
					info={infoText}
					error={getError(slot, setting)}
					texts={getTimeWheelTexts()}
					msStep={getTimeWheelMsStep(device.type)}
				/>

				<Button
					loading={loading[slot][setting]}
					disabled={loading[slot][setting] || getError(slot, setting)}
					size="m"
					width="full"
					on:click={() => set_auto(slot, setting)}>{$format('button_save')}</Button
				>
			{/if}
		{/each}

		{#if slot === 'left'}
			<hr class="mt-l mb-l" />
		{/if}
	{/each}
{/if}
